import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrimaryButton from "components/Button";
import moment from "moment";

const DEFAULT_MESSAGE =
  "Webbieshop is temporarily taking longer than expected. We are reconnecting automatically.";
const OFFLINE_MESSAGE =
  "You appear to be offline. Please check your internet connection.";

// Mirrors the offline check in configs/axiosConfig.js's friendlyErrorMessage —
// this banner can be showing when connectivity drops, so it needs to react to
// online/offline events rather than only checking navigator.onLine once.
const useIsOffline = () => {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" && navigator.onLine === false,
  );
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);
  return isOffline;
};

// Section-level failure state: shown in place of a widget/section that failed
// to load, instead of leaving an endless spinner or a silently blank area.
const ErrorRetry = ({
  message = DEFAULT_MESSAGE,
  onRetry,
  dense = false,
  lastUpdated,
}) => {
  const isOffline = useIsOffline();
  const displayMessage = isOffline ? OFFLINE_MESSAGE : message;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        textAlign: "center",
        py: dense ? 2 : 5,
        px: 2,
      }}
    >
      <Typography sx={{ fontSize: "0.85rem", color: "#6b7280" }}>
        {displayMessage}
      </Typography>
      {lastUpdated && (
        <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
          Last updated {moment(lastUpdated).fromNow()}
        </Typography>
      )}
      {onRetry && (
        <PrimaryButton
          size="sm"
          variant="outlined"
          bgColor="#111827"
          onClick={onRetry}
        >
          <RefreshIcon sx={{ fontSize: 16, mr: 0.75 }} />
          Retry
        </PrimaryButton>
      )}
    </Box>
  );
};

export default ErrorRetry;
